import React, { useState } from 'react';
import contactService from '../../api/contactService';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone_no: '',
    firm_name: '',
    message: '',
    documents: null
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setFormErrors(prev => ({ ...prev, documents: 'Only PDF, DOC, DOCX, JPG, PNG, and WEBP files are allowed' }));
      e.target.value = '';
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setFormErrors(prev => ({ ...prev, documents: 'File size must be less than 5MB' }));
      e.target.value = '';
      return;
    }

    setFormErrors(prev => ({ ...prev, documents: undefined }));
    setFormData(prev => ({
      ...prev,
      documents: file
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    // Validate fullName
    if (!formData.fullName.trim()) {
      errors.fullName = 'Name is required';
    }
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    
    // Validate phone
    const phoneRegex = /^\d{10}$/;
    if (!formData.phone_no.trim()) {
      errors.phone_no = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phone_no.replace(/[\s-]/g, ''))) {
      errors.phone_no = 'Please enter a valid 10-digit phone number';
    }
    
    // Validate firm name
    if (!formData.firm_name.trim()) {
      errors.firm_name = 'Firm name is required';
    }
    
    // Validate message
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Submit form data to API
      await contactService.submitContactForm(formData);
      setSuccess(true);
      resetForm();
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'Failed to submit your request. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      fullName: '',
      email: '',
      phone_no: '',
      firm_name: '',
      message: '',
      documents: null
    });
    setFormErrors({});
  };

  return (
    <div className="w-full bg-[#F9F9FB] py-[60px] md:py-[80px] px-4 md:px-20">
      <div className="max-w-[1262.73px] mx-auto">
        <h2 className="text-[24px] md:text-[29.52px] font-inter font-medium leading-[150%] tracking-[-0.03em] mb-4 md:mb-[60px]">
          Have a query for Agastya Agro? Feel free to ask
        </h2>
        <div className="block md:hidden mb-8">    
          <p className="text-[16px] md:text-[18px] font-inter font-normal leading-[150%] text-[#666666]">
            Didn’t find a role that fits? Submit your resume and be the first to know when new opportunities open up. We’ll reach out if something matches your skills and interests.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-[15px]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-6">
            <div>
              <label htmlFor="fullName" className="block text-[16px] text-gray-600 mb-[10px]">Your Name</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter your name"
                className={`w-full h-[50px] md:h-[60px] rounded-[37px] px-4 text-[16px] focus:outline-none focus:ring-2 ${formErrors.fullName ? 'ring-red-500 border-red-500' : 'focus:ring-[#DB4B12]'}`}
                required
              />
              {formErrors.fullName && (
                <p className="text-red-500 text-xs mt-1 ml-4">{formErrors.fullName}</p>
              )}
            </div>
            <div>
              <label htmlFor="email" className="block text-[16px] text-gray-600 mb-[10px]">Your Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Myemail@gmail.com"
                className={`w-full h-[59.5px] rounded-[37px] px-6 text-[16px] focus:outline-none focus:ring-2 ${formErrors.email ? 'ring-red-500 border-red-500' : 'focus:ring-[#DB4B12]'}`}
                required
              />
              {formErrors.email && (
                <p className="text-red-500 text-xs mt-1 ml-4">{formErrors.email}</p>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-6">
            <div>
              <label htmlFor="phone_no" className="block text-[16px] text-gray-600 mb-[10px]">Your Phone Number</label>
              <input
                type="tel"
                id="phone_no"
                name="phone_no"
                value={formData.phone_no}
                onChange={handleChange}
                placeholder="Enter your phone number"
                className={`w-full h-[50px] md:h-[60px] rounded-[37px] px-4 text-[16px] focus:outline-none focus:ring-2 ${formErrors.phone_no ? 'ring-red-500 border-red-500' : 'focus:ring-[#DB4B12]'}`}
                required
              />
              {formErrors.phone_no && (
                <p className="text-red-500 text-xs mt-1 ml-4">{formErrors.phone_no}</p>
              )}
            </div>
            <div>
              <label htmlFor="firm_name" className="block text-[16px] text-gray-600 mb-[10px]">Firm Name</label>
              <input
                type="text"
                id="firm_name"
                name="firm_name"
                value={formData.firm_name}
                onChange={handleChange}
                placeholder="Enter Firm Name here"
                className={`w-full h-[59.5px] rounded-[37px] px-6 text-[16px] focus:outline-none focus:ring-2 ${formErrors.firm_name ? 'ring-red-500 border-red-500' : 'focus:ring-[#DB4B12]'}`}
                required
              />
              {formErrors.firm_name && (
                <p className="text-red-500 text-xs mt-1 ml-4">{formErrors.firm_name}</p>
              )}
            </div>
          </div>
          
          <div>
            <label htmlFor="message" className="block text-[16px] text-gray-600 mb-[10px]">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Write your Reason to Contact us"
              rows="4"
              className={`w-full h-[150px] rounded-[37px] p-6 text-[16px] focus:outline-none focus:ring-2 ${formErrors.message ? 'ring-red-500 border-red-500' : 'focus:ring-[#DB4B12]'} resize-none`}
              required
            ></textarea>
            {formErrors.message && (
              <p className="text-red-500 text-xs mt-1 ml-4">{formErrors.message}</p>
            )}
          </div>

          <div className="mb-[10px]">
            <p className="text-[16px] text-[#666666] mt-0">
              Upload Any Additional Documents or Images (optional)
            </p>
          </div>
          <div className="max-w-[528px] w-full h-[190.66px] rounded-[37px] flex flex-col p-6 bg-white">
            
            <label className="flex gap-[9px] cursor-pointer text-[14px] text-gray-600 transition-colors ">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                <g opacity="0.2">
                <path d="M15.6604 8.80711L8.94815 15.5194C8.12584 16.3417 7.01056 16.8036 5.84765 16.8036C4.68474 16.8036 3.56946 16.3417 2.74716 15.5194C1.92486 14.6971 1.46289 13.5818 1.46289 12.4189C1.46289 11.256 1.92486 10.1407 2.74716 9.31838L9.00658 3.05896C9.55478 2.50979 10.2987 2.20088 11.0746 2.2002C11.8506 2.19951 12.5951 2.5071 13.1442 3.0553C13.6934 3.60351 14.0023 4.34741 14.003 5.12337C14.0037 5.89933 13.6961 6.64378 13.1479 7.19295L6.87385 13.4524C6.59975 13.7265 6.22799 13.8805 5.84035 13.8805C5.45271 13.8805 5.08095 13.7265 4.80685 13.4524C4.53275 13.1783 4.37876 12.8065 4.37876 12.4189C4.37876 12.0312 4.53275 11.6595 4.80685 11.3854L11.0078 5.19169" stroke="#1C1C1C" strokeWidth="1.46077" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                </svg>
              Choose File or Drop a file Here
            <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
              />
            </label>
            {formData.documents && (
              <p className="text-sm text-gray-500 mt-3">
                {formData.documents.name}
              </p>
            )}
            {formErrors.documents && (
              <p className="text-red-500 text-xs mt-1">{formErrors.documents}</p>
            )}
          </div>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          
          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">Your request has been submitted successfully!</span>
            </div>
          )}
          
          <div className="pt-[4px]">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-black text-white font-inter font-normal text-[16px] rounded-[18.95px] px-4 py-2 h-[37.89px] md:w-[198.65px] flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit Your Request'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
